import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Modal } from '@/components/ui/modal'
import { AvailableDevicePicker } from '@/components/ui/available-device-picker'
import { apiClient } from '@/lib/api-client'
import { toast } from 'react-hot-toast'

interface ReviewDeviceRequestModalProps {
  request: any | null
  onClose: () => void
}

function requestTypeLabel(type?: string) {
  if (type === 'damage') return 'Damage report'
  if (type === 'replace') return 'Replacement'
  return 'New device'
}

export function ReviewDeviceRequestModal({ request, onClose }: ReviewDeviceRequestModalProps) {
  const queryClient = useQueryClient()
  const [deviceId, setDeviceId] = useState('')
  const [notes, setNotes] = useState('')
  const [loading, setLoading] = useState<'assign' | 'reject' | null>(null)

  useEffect(() => {
    setDeviceId('')
    setNotes('')
  }, [request?.id])

  if (!request) return null

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['device-requests'] })
    queryClient.invalidateQueries({ queryKey: ['devices'] })
    queryClient.invalidateQueries({ queryKey: ['assignments'] })
    queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })
  }

  const assign = async () => {
    if (!deviceId) {
      toast.error('Choose an available device')
      return
    }
    setLoading('assign')
    try {
      await apiClient.post(`/device-requests/${request.id}/assign`, {
        deviceId: Number(deviceId),
        reviewNotes: notes.trim() || undefined,
      })
      toast.success('Device assigned. They still need to accept it.')
      refresh()
      onClose()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Could not assign a device')
    } finally {
      setLoading(null)
    }
  }

  const reject = async () => {
    if (notes.trim().length < 3) {
      toast.error('Add a short note before rejecting')
      return
    }
    setLoading('reject')
    try {
      await apiClient.post(`/device-requests/${request.id}/reject`, { reviewNotes: notes.trim() })
      toast.success('Request rejected')
      refresh()
      onClose()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Could not reject the request')
    } finally {
      setLoading(null)
    }
  }

  const current = request.currentDevice
  const currentLabel = current
    ? `${current.deviceModel?.name || current.name}${current.serialNumber ? ` · ${current.serialNumber}` : ''}`
    : null

  return (
    <Modal isOpen={!!request} onClose={onClose} title="Review request" size="lg">
      <div className="space-y-5">
        <div className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm">
          <p className="font-medium">{request.deviceName}</p>
          <p className="mt-1 text-muted-foreground">
            {requestTypeLabel(request.requestType)} from {request.requester?.name || 'Unknown'}
          </p>
          {currentLabel && (
            <p className="mt-2">
              <span className="text-muted-foreground">Current asset: </span>
              {currentLabel}
            </p>
          )}
          <p className="mt-2 whitespace-pre-wrap">{request.reason}</p>
        </div>

        {request.requestType === 'damage' && (
          <p className="rounded-lg border border-border bg-muted/40 px-3 py-2.5 text-sm text-muted-foreground">
            The damage is already recorded on the current asset. It stays with them until you mark that asset returned.
          </p>
        )}

        <div className="space-y-1.5">
          <p className="text-sm font-medium">Assign an available device</p>
          <AvailableDevicePicker value={deviceId} onChange={setDeviceId} />
        </div>

        <Textarea
          label="Note"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          placeholder="Optional for an assignment. Required if you reject."
          rows={3}
        />

        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={!!loading}>
            Close
          </Button>
          <Button type="button" variant="outline" onClick={reject} disabled={!!loading}>
            {loading === 'reject' ? 'Rejecting...' : 'Reject'}
          </Button>
          <Button type="button" onClick={assign} disabled={!!loading}>
            {loading === 'assign' ? 'Assigning...' : 'Assign device'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
